import React, { useEffect, useState } from "react"
import { loadStripe } from "@stripe/stripe-js"
import {
  CardElement,
  useStripe,
  Elements,
  ElementsConsumer,
} from "@stripe/react-stripe-js"
import firebase from "firebase/app"
import { Card, CardBody, Row, Col } from "reactstrap"

import { ErrorBoundary, usePrevious } from "components/Common"
import ModalComponent from "./ModalComponent"
import Config from "config"

const stripePromise = loadStripe(Config.Stripe.PublicKey)

const CheckoutFormAndModalComponent = ({ stateProps, callbacks }) => {
  return (
    <Elements stripe={stripePromise}>
      <ElementsConsumer>
        {({ elements, stripe }) => (
          <>
            <ErrorBoundary>
              <CheckoutForm
                // Payment Related Props
                clientSecret={stateProps.clientSecret}
                handlePaymentChange={callbacks.handlePaymentChange}
                setPaymentRequest={callbacks.setPaymentRequest}
                paymentError={stateProps.paymentError}
                paymentSucceeded={stateProps.paymentSucceeded}
                setCheckoutMethodTitleInAncestor={
                  callbacks.setCheckoutMethodTitle
                }
                // Order Related Props
                totalCost={stateProps.orderData.totalCost}
                discountCost={stateProps.discountCost}
                placeOrder={callbacks.placeOrder}
                // setStateReducer - For Setting States Via Reducer
                setStateReducer={callbacks.setStateReducer}
              />
            </ErrorBoundary>
            <ErrorBoundary>
              <ModalComponent
                // Payment Related Props
                elements={elements}
                stripe={stripe}
                handlePaymentSubmit={callbacks.handlePaymentSubmit}
                paymentRequest={stateProps.paymentRequest}
                checkoutMethodTitle={stateProps.checkoutMethodTitle}
                // Modal Props
                showModal={stateProps.showModal}
                toggleModal={callbacks.toggleModal}
                // Order Related Props
                orderData={stateProps.orderData}
                placeOrder={callbacks.placeOrder}
              />
            </ErrorBoundary>
          </>
        )}
      </ElementsConsumer>
    </Elements>
  )
}

const StripeCE = "stripe-card-element-payment"
const StripePRB = "stripe-payment-request-button-payment"
const getPaymentMethodObj = (
  handlePaymentChange,
  paymentError,
  paymentSucceeded
) => ({
  [StripeCE]: {
    title: StripeCE,
    description: "Credit Card (Stripe)",
    component: (
      <CardElementCheckoutForm
        handlePaymentChange={handlePaymentChange}
        paymentError={paymentError}
        paymentSucceeded={paymentSucceeded}
      />
    ),
    info: "Enter Card Details And Select 'Next' To Continue",
  },
  [StripePRB]: {
    title: StripePRB,
    description: "Apple Pay / Google Pay",
    info: "Select 'Next' To Continue",
  },
})

const CheckoutForm = (
  // Ancestor - ScheduleOrder
  {
    clientSecret,
    handlePaymentChange, // Setting paymentError State In Ancestor
    paymentError,
    paymentSucceeded,
    setPaymentRequest, // Setting State In Ancestor
    setCheckoutMethodTitleInAncestor, // Setting State In Ancestor
    totalCost,
    discountCost,
    placeOrder,
    setStateReducer,
  }
) => {
  const paymentMethodObj = getPaymentMethodObj(
    handlePaymentChange,
    paymentError,
    paymentSucceeded
  )

  // ===== paymentRequest useEffect =====
  const paymentRequest = usePaymentRequest({
    clientSecret,
    discountCost,
    totalCost,
    placeOrder,
  }) // Custom Hook
  const prevPaymentRequest = usePrevious(paymentRequest)
  useEffect(() => {
    if (
      paymentRequest &&
      JSON.stringify(paymentRequest) !== JSON.stringify(prevPaymentRequest)
    ) {
      setPaymentRequest(paymentRequest)
    }
  })

  // ===== paymentMethod =====
  const [paymentMethod, setPaymentMethod] = useState([StripeCE])
  useEffect(() => {
    console.log("CheckoutForm - useEffect - setPaymentMethod")
    if (paymentRequest && !(paymentMethod.indexOf(StripePRB) > -1)) {
      setPaymentMethod((prevState) => [StripePRB, ...prevState])
    }
  }, [paymentRequest, paymentMethod])

  const [checkoutMethod, setCheckoutMethod] = useState(null) // This Is Payment Method Component
  const [checkoutMethodInfo, setCheckoutMethodInfo] = useState(null) // Info In String Form

  // ===== checkoutMethodTitle - StripeCE && StripePRB =====
  const [checkoutMethodTitle, setCheckoutMethodTitle] = useState(null)
  useEffect(() => {
    if (checkoutMethodTitle) {
      setCheckoutMethodTitleInAncestor(checkoutMethodTitle) // ToDo: Remove React Hooks Warning
      setStateReducer("paymentError", null) // ToDo: Remove React Hooks Warning
    }
  }, [checkoutMethodTitle])

  // ===== Passing Down paymentError =====
  const prevPaymentError = usePrevious(paymentError)
  useEffect(() => {
    if (paymentError && paymentError !== prevPaymentError) {
      setCheckoutMethod(paymentMethodObj[checkoutMethodTitle].component)
    }
  })

  return (
    <>
      <Row className="align-items-center text-center justify-content-center">
        {paymentMethod &&
          paymentMethod.map((item, idx) => {
            const backgroundColor =
              checkoutMethodTitle === item ? "#eaf8ff" : ""
            const border =
              checkoutMethodTitle === item ? "1px solid #3262A0" : ""
            const singlePaymentMethodObj = paymentMethodObj[item]
            return (
              <Col md="4" key={idx}>
                <Card
                  className=""
                  style={{
                    cursor: "pointer",
                    backgroundColor,
                    border,
                  }}
                  onClick={() => {
                    if (singlePaymentMethodObj) {
                      if (singlePaymentMethodObj.title === StripeCE) {
                        setCheckoutMethod(singlePaymentMethodObj.component)
                      } else {
                        setCheckoutMethod(null)
                      }
                      setCheckoutMethodTitle(singlePaymentMethodObj.title)
                      setCheckoutMethodInfo(singlePaymentMethodObj.info)
                    }
                  }}
                >
                  <CardBody>
                    {singlePaymentMethodObj &&
                      singlePaymentMethodObj.description}
                  </CardBody>
                </Card>
              </Col>
            )
          })}
      </Row>
      {checkoutMethod}
      <Row className="text-center">
        <Col sm="12">{checkoutMethodInfo}</Col>
      </Row>
    </>
  )
}

const StripeCardElementStyle = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: "Arial, sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#32325d",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
}

const CardElementCheckoutForm = ({
  handlePaymentChange,
  paymentError,
  paymentSucceeded,
}) => {
  return (
    <>
      <Row className="mb-3">
        <Col md={{ size: 8, offset: 2 }}>
          <CardElement
            id="card-element"
            options={StripeCardElementStyle}
            onChange={handlePaymentChange}
          />
        </Col>
        <Col md="12" className="text-center">
          <br />
          {paymentError && (
            <div className="card-error" role="alert">
              {paymentError}
            </div>
          )}
          {paymentSucceeded && <div>Payment Successful</div>}
        </Col>
      </Row>
    </>
  )
}

// ===== ===== ===== ===== ===== ===== ======
// ===== Custom Hook For paymentRequest =====
// ===== ===== ===== ===== ===== ===== ======
const usePaymentRequest = (
  // Ancestor - ScheduleOrder
  {
    clientSecret, // Fetched When `ScheduleOrder` Mounts And Fetched AGAIN When Promo Code Is Applied
    discountCost, // Prop From Ancestor
    totalCost, // Prop From Ancestor
    placeOrder, // For Placing Order Via Ancestor
  }
) => {
  const stripe = useStripe()
  const [paymentRequest, setPaymentRequest] = useState(null)

  // ===== Setting Payment Request =====
  useEffect(() => {
    if (stripe && totalCost > 0) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Final Cost",
          amount: (totalCost - discountCost) * 100,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })

      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr)
        }
      })
    }
  }, [stripe, totalCost, discountCost])

  // ===== Making Payment Via Stripe =====
  useEffect(() => {
    if (clientSecret && paymentRequest && stripe) {
      paymentRequest.on("paymentmethod", async (ev) => {
        const {
          paymentIntent,
          error: confirmError,
        } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        )

        if (confirmError) {
          // Report to the browser that the payment failed, prompting it to
          // re-show the payment interface, or show an error message and close
          // the payment interface.
          console.log("usePaymentRequest - Payment Failed - 1")
          ev.complete("fail")
        } else {
          // Report to the browser that the confirmation was successful, prompting
          // it to close the browser payment method collection interface.
          ev.complete("success")
          // Check if the PaymentIntent requires any actions and if so let Stripe.js
          // handle the flow. If using an API version older than "2019-02-11" instead
          // instead check for: `paymentIntent.status === "requires_source_action"`.
          if (paymentIntent.status === "requires_action") {
            // Let Stripe.js handle the rest of the payment flow.
            const { error } = await stripe.confirmCardPayment(clientSecret)
            if (error) {
              console.log("usePaymentRequest - Payment Failed - 2")
              // The payment failed - ask your customer for a new payment method.
            } else {
              // The payment has succeeded.
              console.log("usePaymentRequest - Payment Successful - 1")
            }
          } else {
            // The payment has succeeded.
            console.log("usePaymentRequest - Payment Successful - 2")
            placeOrder(paymentIntent, true) // ToDo: Remove React Hooks Warning
            firebase.analytics().logEvent("payment_tried")
          }
        }
      })
      return () => {
        if (paymentRequest) {
          paymentRequest.removeAllListeners() // Other Methods: removeEventListener, removeListener - Tried.
        }
      }
    }
  }, [clientSecret, paymentRequest, stripe])

  return paymentRequest
}

export default CheckoutFormAndModalComponent
